import React from 'react';
import type { TFunction } from 'i18next';
import moment from 'moment';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import { GetParamsDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  ParamsGroupObj,
  ParamsNameObj,
} from '../../../common/pokCore/validation/enums';

interface ParamsSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
}

export const paramsForGrid = (
  t: TFunction<'administrationLng', 'administrationLng'>,
  tk: TKeys<'administrationLng'>,
  param: GetParamsDto,
) => {
  const getValue = () => {
    switch (param.type) {
      case 'String':
        return param.valueString || '';
      case 'Boolean':
        return param.valueBoolean ? t(tk.common.yes) : t(tk.common.no);
      case 'Number':
        return param.valueNumber?.toString() || '';
      case 'Date':
        return param.valueDate
          ? moment(param.valueDate)
              .locale(t(tk.common.localeShort))
              .format('LLL')
          : '';
      default:
        return '';
    }
  };
  return {
    key: param.id,
    values: {
      group_text: t(ParamsGroupObj[param.group]),
      name_text: t(ParamsNameObj[param.name]),
      'params.type': param.type,
      'params.value': getValue(),
      'params.isEditable': param.isEditable
        ? t(tk.common.yes)
        : t(tk.common.no),
      'owner.name': param.owner?.name || '',
    },
    isInactive: !param.isVisible,
  };
};

export const ParamsSearch: React.FC<ParamsSearchProps> = props => {
  const { t, tk } = useTranslation('administrationLng');

  const columns: GridColumn[] = [
    { header: t(tk.administration.group), property: 'group_text' },
    { header: t(tk.common.name), property: 'name_text' },
    {
      header: t(tk.administration.type),
      property: 'params.type',
      noFilter: true,
    },
    {
      header: t(tk.administration.value),
      property: 'params.value',
      noFilter: true,
      noSorting: true,
    },
    {
      header: t(tk.administration.editable),
      property: 'params.isEditable',
      noFilter: true,
    },
    { header: t(tk.common.user), property: 'owner.name' },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="group_text"
      initialOrderDirection="ASC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.administration.noParameters)}
      onRowClick={props.onRowClick}
      withHistory={true}
      refresh={props.refresh}
      withFilterInColumns={true}
    />
  );
};
